import classNames from 'classnames';
import type { ChangeEventHandler } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import {
  serializedToExportedString,
  type SerializedValue,
  type SerializeValue,
} from '../../thread/ThreadMessage/useSerializeValue';
import { ChatInput } from '../chat/ChatInput';
import type { UseGraphFieldArgument } from './useGraphField';
import { useGraphField } from './useGraphField';

export interface EditorFieldSimpleRef {
  reset: VoidFunction;
  blur: VoidFunction;
  focus: VoidFunction;
}

interface EditorFieldExtraProps {
  name: string;
  className?: string | undefined;
  placeholder?: string;
  loading?: boolean;
  onSubmit?: VoidFunction;

  initialValue: SerializedValue;
  serializeValue: SerializeValue;
  hasCancelButton?: boolean;
}

export const EditorFieldSimple = forwardRef<EditorFieldSimpleRef, EditorFieldExtraProps>(
  (
    { name, className, placeholder, loading, initialValue, serializeValue, hasCancelButton },
    ref,
  ) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const options: UseGraphFieldArgument<SerializedValue> = useMemo(
      () => ({
        name,
        config: { initialValue },
      }),
      [initialValue, name],
    );
    const {
      input: { value: content, onChange, onFocus, onBlur },
      computedError,
    } = useGraphField<SerializedValue, HTMLTextAreaElement>(options);

    const onChangeSerialized = useCallback(
      (value: string | null) => {
        onChange?.(value);
        serializeValue(value);
      },
      [onChange, serializeValue],
    );

    const onChangeEvent = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
      e => {
        onChangeSerialized(e.target.value);
      },
      [onChangeSerialized],
    );

    useImperativeHandle(
      ref,
      () => ({
        reset: () => onChangeSerialized(null),
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        addContent: (emoji: string) => {
          const currentValue = inputRef.current?.value || '';
          onChangeSerialized(currentValue + emoji);
        },
      }),
      [onChangeSerialized],
    );

    const contentStr = useMemo(() => serializedToExportedString(content), [content]);

    return (
      <>
        <ChatInput
          ref={inputRef}
          className={classNames(
            'appearance-none resize-none py-3 outline-none bg-gray-lighter flex-1 sm:text-base rounded-3xl',
            className,
            hasCancelButton ? 'pl-4 pr-10' : 'px-4',
          )}
          value={contentStr || ''}
          disabled={loading}
          onChange={onChangeEvent}
          placeholder={placeholder}
          // enter={onSubmit}
          onFocus={onFocus}
          onBlur={onBlur}
          large
          autoFocus
        />

        {computedError ? (
          <p className="text-primary-base border-none mb-3 text-xs ">
            <FormattedMessage {...(computedError as MessageDescriptor)} />
          </p>
        ) : null}
      </>
    );
  },
);
